import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import { NodeSelection } from 'prosemirror-state';
import type { ReactNode } from 'react';
import { useContext, useState } from 'react';
import { Button } from '../../atom/Button/Button';
import { Input } from '../../atom/Input/Input';
import { InputDecorator } from '../../atom/InputDecorator/InputDecorator';
import { Modal } from '../../atom/Modal/Modal';
import { Radio } from '../../atom/Radio/Radio';
import { CustomEditorContext } from '.';
import BoxedTextIcon from './_assets/boxed-text.svg';
import HrIcon from './_assets/hr.svg';
import type { ImageButtonProps } from './_components/ImageButton';
import { ImageButton } from './_components/ImageButton';
import { TEXT_ALIGN } from './_constants';
import { CLASS_TO_BUTTON_SIZE, CLASS_TO_BUTTON_THEME, type CustomLinkOptions } from './_utils/CustomLink';
import { FONT_SIZES, type FontSizeType } from './_utils/FontSize';
import { FONT_WEIGHTS } from './_utils/FontWeight';
import { HIGHLIGHT_LABELS, HIGHLIGHTS } from './_utils/Highlight';
import { TEXT_COLORS } from './_utils/TextColor';
export interface ToolbarProps {
  imageUploadOptions?: ImageButtonProps;
  children?: ReactNode;
}
type Level = 2 | 3 | 4 | 5 | 6;
const HEADING_OPTIONS = [{
  value: 'paragraph',
  label: '본문'
}, {
  value: 'h2',
  label: '제목 2'
}, {
  value: 'h3',
  label: '제목 3'
}, {
  value: 'h4',
  label: '제목 4'
}, {
  value: 'h5',
  label: '제목 5'
}, {
  value: 'h6',
  label: '제목 6'
}];
export const Toolbar = t0 => {
  const $ = _c(126);
  const {
    imageUploadOptions,
    children
  } = t0;
  const {
    editor
  } = useContext(CustomEditorContext);
  const [isLinkModalOpened, setIsLinkModalOpened] = useState(false);
  const isImageSelected = editor?.state.selection instanceof NodeSelection && editor?.state.selection.node.type.name === "image";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      theme: "text",
      size: "H56",
      isOutLink: false,
      href: ""
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [customLinkOptions, setCustomLinkOptions] = useState(t1);
  let t2;
  if ($[1] !== editor || $[2] !== isImageSelected) {
    t2 = () => {
      if (isImageSelected) {
        const imageAttrs = editor?.state.selection instanceof NodeSelection ? editor.state.selection.node.attrs : null;
        setCustomLinkOptions(prev => ({
          ...prev,
          theme: "image",
          href: imageAttrs?.href || "",
          isOutLink: imageAttrs?.target === "_blank"
        }));
      } else {
        if (editor?.isActive("link")) {
          const linkAttrs = editor.getAttributes("link");
          const linkClass = linkAttrs.class || "";
          const theme = Object.entries(CLASS_TO_BUTTON_THEME).find(t3 => {
            const [cls] = t3;
            return linkClass.includes(cls);
          })?.[1] as CustomLinkOptions['theme'] ?? "text";
          const size = Object.entries(CLASS_TO_BUTTON_SIZE).find(t4 => {
            const [cls_0] = t4;
            return linkClass.includes(cls_0);
          })?.[1] as CustomLinkOptions['size'] ?? "H56";
          setCustomLinkOptions({
            theme,
            size,
            href: linkAttrs.href || "",
            isOutLink: linkAttrs.target === "_blank"
          });
        } else {
          setCustomLinkOptions({
            theme: "text",
            size: "H56",
            href: "",
            isOutLink: false
          });
        }
      }
      setIsLinkModalOpened(true);
    };
    $[1] = editor;
    $[2] = isImageSelected;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleLinkButtonClick = t2;
  if (!editor) {
    return null;
  }
  let t3;
  if ($[4] !== editor) {
    t3 = () => {
      for (let i = 2; i <= 6; i++) {
        if (editor.isActive("heading", {
          level: i
        })) {
          return `h${i}`;
        }
      }
      return "paragraph";
    };
    $[4] = editor;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const getSelectedHeading = t3;
  const selectedHeading = getSelectedHeading();
  let t4;
  if ($[6] !== editor) {
    t4 = TEXT_COLORS.find(t5 => {
      const [, color] = t5;
      return editor.isActive("textStyle", {
        color
      });
    }) ?? [];
    $[6] = editor;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const [selectedColor] = t4;
  let t5;
  if ($[8] !== editor) {
    t5 = FONT_WEIGHTS.find(fontWeight => editor.isActive("fontWeight", {
      fontWeight
    }));
    $[8] = editor;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const selectedFontWeight = t5;
  let t6;
  if ($[10] !== editor) {
    t6 = FONT_SIZES.find(fontSize => editor.isActive("fontSize", {
      fontSize
    }));
    $[10] = editor;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const selectedFontSize = t6;
  let isBoxedText;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t7;
  let t8;
  let t9;
  if ($[12] !== editor || $[13] !== selectedColor || $[14] !== selectedFontSize || $[15] !== selectedFontWeight || $[16] !== selectedHeading) {
    isBoxedText = editor.isActive("boxedText");
    let t17;
    if ($[28] !== editor) {
      t17 = TEXT_ALIGN.find(textAlign => editor.isActive("textAlign", {
        textAlign
      }));
      $[28] = editor;
      $[29] = t17;
    } else {
      t17 = $[29];
    }
    const selectedTextAlign = t17;
    t9 = "flex flex-wrap items-center justify-center gap-8 bg-Gray-100 p-8";
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <p className="text-Gray-800 typo-caption1">*h1 태그는 제목 고정입니다.</p>;
      $[30] = t10;
    } else {
      t10 = $[30];
    }
    let t18;
    if ($[31] !== editor) {
      t18 = e => {
        const value = e.target.value;
        if (value === "paragraph") {
          editor.chain().focus().setParagraph().run();
        } else {
          const level = Number.parseInt(value.substring(1)) as Level;
          editor.chain().focus().setHeading({
            level
          }).run();
        }
      };
      $[31] = editor;
      $[32] = t18;
    } else {
      t18 = $[32];
    }
    let t19;
    if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = HEADING_OPTIONS.map(_temp);
      $[33] = t19;
    } else {
      t19 = $[33];
    }
    if ($[34] !== selectedHeading || $[35] !== t18) {
      t11 = <select aria-label={"\uC81C\uBAA9 \uC2A4\uD0C0\uC77C"} value={selectedHeading} onChange={t18}>{t19}</select>;
      $[34] = selectedHeading;
      $[35] = t18;
      $[36] = t11;
    } else {
      t11 = $[36];
    }
    let t20;
    if ($[37] !== editor) {
      t20 = e_0 => {
        editor.chain().focus().setFontSize(e_0.target.value as FontSizeType).run();
      };
      $[37] = editor;
      $[38] = t20;
    } else {
      t20 = $[38];
    }
    let t21;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = FONT_SIZES.map(_temp2);
      $[39] = t21;
    } else {
      t21 = $[39];
    }
    if ($[40] !== selectedFontSize || $[41] !== t20) {
      t12 = <select aria-label={"\uAE00\uC790 \uD06C\uAE30"} value={selectedFontSize} onChange={t20}>{t21}</select>;
      $[40] = selectedFontSize;
      $[41] = t20;
      $[42] = t12;
    } else {
      t12 = $[42];
    }
    let t22;
    if ($[43] !== editor) {
      t22 = e_1 => {
        editor?.chain().focus().setTextAlign(e_1.target.value).run();
      };
      $[43] = editor;
      $[44] = t22;
    } else {
      t22 = $[44];
    }
    let t23;
    if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = TEXT_ALIGN.map(_temp3);
      $[45] = t23;
    } else {
      t23 = $[45];
    }
    if ($[46] !== selectedTextAlign || $[47] !== t22) {
      t13 = <select aria-label={"\uC815\uB82C"} value={selectedTextAlign} onChange={t22}>{t23}</select>;
      $[46] = selectedTextAlign;
      $[47] = t22;
      $[48] = t13;
    } else {
      t13 = $[48];
    }
    let t24;
    if ($[49] !== editor || $[50] !== selectedColor) {
      t24 = TEXT_COLORS.map(t25 => {
        const [label, color_0] = t25;
        return <button key={label} className={cn("font-800", {
          underline: selectedColor === label
        })} style={{
          color: color_0
        }} type="button" onClick={() => editor?.chain().focus().setColor(color_0).run()}>A</button>;
      });
      $[49] = editor;
      $[50] = selectedColor;
      $[51] = t24;
    } else {
      t24 = $[51];
    }
    if ($[52] !== t24) {
      t14 = <section className="flex gap-8 rounded-xsmall border-1 border-Gray-600 px-8 py-2">{t24}</section>;
      $[52] = t24;
      $[53] = t14;
    } else {
      t14 = $[53];
    }
    let t25;
    if ($[54] !== editor || $[55] !== selectedFontWeight) {
      t25 = FONT_WEIGHTS.map(fontWeight_0 => <button key={fontWeight_0} className={cn(fontWeight_0, {
        underline: selectedFontWeight === fontWeight_0
      })} type="button" onClick={() => editor?.chain().focus().setFontWeight(fontWeight_0).run()}>B</button>);
      $[54] = editor;
      $[55] = selectedFontWeight;
      $[56] = t25;
    } else {
      t25 = $[56];
    }
    if ($[57] !== t25) {
      t15 = <section className="flex gap-8 rounded-xsmall border-1 border-Gray-600 px-8 py-2">{t25}</section>;
      $[57] = t25;
      $[58] = t15;
    } else {
      t15 = $[58];
    }
    if ($[59] !== editor) {
      t16 = HIGHLIGHTS.map(highlight => {
        const isActive = editor.isActive("highlight", {
          highlight
        });
        return <button key={highlight} aria-label={HIGHLIGHT_LABELS[highlight]} className={cn(highlight, "flex size-24 min-h-24 min-w-24 items-center justify-center rounded-xsmall font-700", {
          "border-1 border-Gray-800": isActive
        })} type="button" onClick={() => isActive ? editor.chain().focus().unsetHighlight().run() : editor.chain().focus().setHighlight(highlight).run()} />;
      });
      $[59] = editor;
      $[60] = t16;
    } else {
      t16 = $[60];
    }
    t7 = "\uBC15\uC2A4\uD14D\uC2A4\uD2B8";
    t8 = cn("box-border flex size-24 items-center justify-center", {
      "border-Gray-800 border-1 rounded-xxsmall": isBoxedText
    });
    $[12] = editor;
    $[13] = selectedColor;
    $[14] = selectedFontSize;
    $[15] = selectedFontWeight;
    $[16] = selectedHeading;
    $[17] = isBoxedText;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
    $[21] = t13;
    $[22] = t14;
    $[23] = t15;
    $[24] = t16;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    isBoxedText = $[17];
    t10 = $[18];
    t11 = $[19];
    t12 = $[20];
    t13 = $[21];
    t14 = $[22];
    t15 = $[23];
    t16 = $[24];
    t7 = $[25];
    t8 = $[26];
    t9 = $[27];
  }
  let t17;
  if ($[61] !== editor || $[62] !== isBoxedText) {
    t17 = () => isBoxedText ? editor.chain().focus().unsetBoxedText().run() : editor.chain().focus().setBoxedText().run();
    $[61] = editor;
    $[62] = isBoxedText;
    $[63] = t17;
  } else {
    t17 = $[63];
  }
  let t18;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <BoxedTextIcon />;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  let t19;
  if ($[65] !== t17 || $[66] !== t7 || $[67] !== t8) {
    t19 = <button aria-label={t7} className={t8} type="button" onClick={t17}>{t18}</button>;
    $[65] = t17;
    $[66] = t7;
    $[67] = t8;
    $[68] = t19;
  } else {
    t19 = $[68];
  }
  let t20;
  if ($[69] !== editor) {
    t20 = () => {
      editor?.chain().focus().setHorizontalRule().run();
    };
    $[69] = editor;
    $[70] = t20;
  } else {
    t20 = $[70];
  }
  let t21;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <HrIcon />;
    $[71] = t21;
  } else {
    t21 = $[71];
  }
  let t22;
  if ($[72] !== t20) {
    t22 = <button aria-label={"\uAD6C\uBD84\uC120 \uC0BD\uC785"} type="button" onClick={t20}>{t21}</button>;
    $[72] = t20;
    $[73] = t22;
  } else {
    t22 = $[73];
  }
  let t23;
  if ($[74] !== imageUploadOptions) {
    t23 = <ImageButton {...imageUploadOptions} />;
    $[74] = imageUploadOptions;
    $[75] = t23;
  } else {
    t23 = $[75];
  }
  let t24;
  if ($[76] !== handleLinkButtonClick) {
    t24 = <Button dimensions="H26" onClick={handleLinkButtonClick}>링크</Button>;
    $[76] = handleLinkButtonClick;
    $[77] = t24;
  } else {
    t24 = $[77];
  }
  let t25;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => setIsLinkModalOpened(false);
    $[78] = t25;
  } else {
    t25 = $[78];
  }
  let t26;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = e_2 => {
      setCustomLinkOptions(prev_0 => ({
        ...prev_0,
        href: e_2.target.value
      }));
    };
    $[79] = t26;
  } else {
    t26 = $[79];
  }
  let t27;
  if ($[80] !== customLinkOptions.href) {
    t27 = <InputDecorator label={"\uC8FC\uC18C"}><Input value={customLinkOptions.href} onChange={t26} /></InputDecorator>;
    $[80] = customLinkOptions.href;
    $[81] = t27;
  } else {
    t27 = $[81];
  }
  let t28;
  let t29;
  let t30;
  let t31;
  let t32;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = value_0 => {
      setCustomLinkOptions(prev_1 => ({
        ...prev_1,
        theme: value_0
      }));
    };
    t29 = <Radio.Item value="text">문자</Radio.Item>;
    t30 = <Radio.Item value="primary-fill-button">버튼 1</Radio.Item>;
    t31 = <Radio.Item value="sub-fill-button">버튼 2</Radio.Item>;
    t32 = <Radio.Item value="image">이미지</Radio.Item>;
    $[82] = t28;
    $[83] = t29;
    $[84] = t30;
    $[85] = t31;
    $[86] = t32;
  } else {
    t28 = $[82];
    t29 = $[83];
    t30 = $[84];
    t31 = $[85];
    t32 = $[86];
  }
  let t33;
  if ($[87] !== customLinkOptions.theme) {
    t33 = <InputDecorator label={"\uB9C1\uD06C \uD615\uC2DD"}><Radio.Group value={customLinkOptions.theme} onChange={t28}>{t29}{t30}{t31}{t32}</Radio.Group></InputDecorator>;
    $[87] = customLinkOptions.theme;
    $[88] = t33;
  } else {
    t33 = $[88];
  }
  const t34 = customLinkOptions.isOutLink ? "out" : "in";
  let t35;
  let t36;
  let t37;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = value_1 => {
      setCustomLinkOptions(prev_2 => ({
        ...prev_2,
        isOutLink: value_1 === "out"
      }));
    };
    t36 = <Radio.Item value="out">외부에서 열기</Radio.Item>;
    t37 = <Radio.Item value="in">내부에서 열기</Radio.Item>;
    $[89] = t35;
    $[90] = t36;
    $[91] = t37;
  } else {
    t35 = $[89];
    t36 = $[90];
    t37 = $[91];
  }
  let t38;
  if ($[92] !== t34) {
    t38 = <InputDecorator label={"\uCC3D \uC5F4\uAE30"}><Radio.Group value={t34} onChange={t35}>{t36}{t37}</Radio.Group></InputDecorator>;
    $[92] = t34;
    $[93] = t38;
  } else {
    t38 = $[93];
  }
  let t39;
  if ($[94] !== t27 || $[95] !== t33 || $[96] !== t38) {
    t39 = <article className="mb-32 flex flex-col gap-16">{t27}{t33}{t38}</article>;
    $[94] = t27;
    $[95] = t33;
    $[96] = t38;
    $[97] = t39;
  } else {
    t39 = $[97];
  }
  const t40 = !customLinkOptions.href;
  let t41;
  if ($[98] !== customLinkOptions || $[99] !== editor) {
    t41 = () => {
      setIsLinkModalOpened(false);
      editor.chain().focus().setCustomLink(customLinkOptions).run();
    };
    $[98] = customLinkOptions;
    $[99] = editor;
    $[100] = t41;
  } else {
    t41 = $[100];
  }
  let t42;
  if ($[101] !== t40 || $[102] !== t41) {
    t42 = <Button className="flex-1" disabled={t40} onClick={t41}>설정</Button>;
    $[101] = t40;
    $[102] = t41;
    $[103] = t42;
  } else {
    t42 = $[103];
  }
  let t43;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <Button className="flex-1" theme="sub-fill" onClick={() => {
      setIsLinkModalOpened(false);
    }}>취소</Button>;
    $[104] = t43;
  } else {
    t43 = $[104];
  }
  let t44;
  if ($[105] !== t42) {
    t44 = <footer className="flex w-full gap-8">{t42}{t43}</footer>;
    $[105] = t42;
    $[106] = t44;
  } else {
    t44 = $[106];
  }
  let t45;
  if ($[107] !== isLinkModalOpened || $[108] !== t39 || $[109] !== t44) {
    t45 = <Modal opened={isLinkModalOpened} onClose={t25}>{t39}{t44}</Modal>;
    $[107] = isLinkModalOpened;
    $[108] = t39;
    $[109] = t44;
    $[110] = t45;
  } else {
    t45 = $[110];
  }
  let t46;
  if ($[111] !== children || $[112] !== t10 || $[113] !== t11 || $[114] !== t12 || $[115] !== t13 || $[116] !== t14 || $[117] !== t15 || $[118] !== t16 || $[119] !== t19 || $[120] !== t22 || $[121] !== t23 || $[122] !== t24 || $[123] !== t45 || $[124] !== t9) {
    t46 = <nav className={t9}>{t10}{t11}{t12}{t13}{t14}{t15}{t16}{t19}{t22}{t23}{t24}{t45}{children}</nav>;
    $[111] = children;
    $[112] = t10;
    $[113] = t11;
    $[114] = t12;
    $[115] = t13;
    $[116] = t14;
    $[117] = t15;
    $[118] = t16;
    $[119] = t19;
    $[120] = t22;
    $[121] = t23;
    $[122] = t24;
    $[123] = t45;
    $[124] = t9;
    $[125] = t46;
  } else {
    t46 = $[125];
  }
  return t46;
};
function _temp(option) {
  return <option key={option.value} value={option.value}>{option.label}</option>;
}
function _temp2(fontSize_0) {
  return <option key={fontSize_0} className={fontSize_0} value={fontSize_0}>{fontSize_0}</option>;
}
function _temp3(textAlign_0) {
  return <option key={textAlign_0} value={textAlign_0}>{textAlign_0}</option>;
}